import React, { useState, useRef, useEffect, useMemo } from 'react';
import api from '../../utils/api';

const SupportChat = ({ studio, course }) => {
  const studioSlug = studio?.slug;
  const courseId = course?._id;
  // Per-offering opt-in: studios enable the chat per course in Settings → AI Assistant.
  // The backend also enforces a studio-slug allowlist (TFATX in prod, any in dev).
  const enabled = !!studioSlug && !!courseId && !!course?.supportChatEnabled;

  // Session id threads turns from the same browser/prospect into one
  // SupportConversation doc (Kishan-internal v1 — used by
  // scripts/read-support-conversations.js, not surfaced in the UI yet). Per
  // (studio, course) so different offerings stay separate threads.
  // Falls back to an in-memory UUID if localStorage is blocked, so the
  // backend's required-sessionId check never breaks the chat.
  const sessionId = useMemo(() => {
    if (!studioSlug || !courseId) return null;
    const makeUuid = () => (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
          const r = (Math.random() * 16) | 0;
          return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
        });
    const key = `dh_support_session_${studioSlug}_${courseId}`;
    try {
      let id = window.localStorage.getItem(key);
      if (!id) {
        id = makeUuid();
        window.localStorage.setItem(key, id);
      }
      return id;
    } catch {
      return makeUuid();
    }
  }, [studioSlug, courseId]);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('chat'); // 'chat' | 'email' | 'sent'
  const [messages, setMessages] = useState([
    { role: 'assistant', content: `Hi! I can answer quick questions about ${course?.name || 'this offering'}. For anything specific, use "Email us" below and someone from the studio will get back to you.` }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [emailForm, setEmailForm] = useState({ name: '', email: '', question: '' });
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Nudge bubble — appears 2 seconds after page load if the chat is enabled
  // and the panel hasn't been opened yet, hinting that help is one tap away.
  // Auto-dismisses after 10s, or on the user opening / dismissing it.
  const [nudgeShown, setNudgeShown] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);

  const scrollRef = useRef(null);
  useEffect(() => {
    if (mode === 'chat' && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, mode, loading]);

  useEffect(() => {
    if (!enabled || nudgeDismissed) return;
    const inTimer = setTimeout(() => setNudgeShown(true), 2000);
    const outTimer = setTimeout(() => setNudgeShown(false), 12000);
    return () => { clearTimeout(inTimer); clearTimeout(outTimer); };
  }, [enabled, nudgeDismissed]);

  if (!enabled) return null;

  const dismissNudge = (e) => {
    if (e) e.stopPropagation();
    setNudgeShown(false);
    setNudgeDismissed(true);
  };

  const closePanel = () => {
    setOpen(false);
    setError('');
    setEmailError('');
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setLoading(true);
    setError('');

    try {
      const apiMessages = next
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .slice(-10)
        .map(m => ({ role: m.role, content: m.content }));

      const { data } = await api.post('/support/ask', {
        studioSlug,
        courseId,
        sessionId,
        messages: apiMessages
      });
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Something went wrong. Try Email us instead.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const openEmail = () => {
    const lastUser = [...messages].reverse().find(m => m.role === 'user');
    setEmailForm(prev => ({
      ...prev,
      question: prev.question || lastUser?.content || ''
    }));
    setMode('email');
    setEmailError('');
  };

  const sendEmail = async () => {
    if (emailSending) return;
    if (!emailForm.email.trim() || !/^\S+@\S+\.\S+$/.test(emailForm.email.trim())) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    if (emailForm.question.trim().length < 5) {
      setEmailError('Please write at least a sentence so the studio can help.');
      return;
    }
    setEmailSending(true);
    setEmailError('');

    try {
      const transcript = messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content }));

      await api.post('/support/email', {
        studioSlug,
        courseId,
        sessionId: sessionId || undefined,
        studentEmail: emailForm.email.trim(),
        studentName: emailForm.name.trim() || undefined,
        question: emailForm.question.trim(),
        transcript: transcript.length ? transcript : undefined
      });
      setMode('sent');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Could not send. Please try again.';
      setEmailError(msg);
    } finally {
      setEmailSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating launcher + optional nudge bubble */}
      {!open && (
        <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-3">
          {nudgeShown && (
            <button
              type="button"
              onClick={() => { dismissNudge(); setOpen(true); }}
              className="group relative max-w-[260px] bg-white border border-gray-200 shadow-xl rounded-2xl rounded-br-sm px-4 py-3 pr-9 text-left text-sm text-gray-800 hover:bg-gray-50 transition-colors"
              aria-label="Open support chat"
            >
              <span className="block font-medium leading-snug">Have a question?</span>
              <span className="block text-xs text-gray-500 mt-0.5">Ask me anything about {course?.name || 'this class'}.</span>
              <span
                role="button"
                tabIndex={-1}
                aria-label="Dismiss"
                onClick={dismissNudge}
                className="absolute top-2 right-2 w-5 h-5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center text-xs"
              >
                ×
              </span>
            </button>
          )}
          <button
            type="button"
            aria-label="Open support chat"
            onClick={() => { dismissNudge(); setOpen(true); }}
            className="relative w-16 h-16 rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-lg flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2"
          >
            {nudgeShown && (
              <span className="absolute inset-0 rounded-full bg-brand-500 opacity-50 animate-ping" />
            )}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="relative w-7 h-7" aria-hidden="true">
              <path fillRule="evenodd" d="M4.804 21.644A6.707 6.707 0 0 0 6 21.75a6.721 6.721 0 0 0 3.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 0 1-.814 1.686.75.75 0 0 0 .44 1.223Z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed inset-x-0 bottom-0 sm:inset-x-auto sm:right-4 sm:bottom-4 z-40 bg-white shadow-2xl border border-gray-200 flex flex-col rounded-t-2xl sm:rounded-2xl sm:w-96 max-h-[85vh] sm:max-h-[600px] sm:h-[600px]"
          role="dialog"
          aria-label="Support chat"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-brand-500 text-white rounded-t-2xl">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">Ask about {course?.name || 'this class'}</div>
              <div className="text-[11px] text-brand-100 truncate">{studio?.name || ''} • AI-assisted</div>
            </div>
            <button
              type="button"
              aria-label="Close support chat"
              onClick={closePanel}
              className="ml-2 shrink-0 w-8 h-8 rounded-full hover:bg-brand-600 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white/40"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden="true">
                <path d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z" />
              </svg>
            </button>
          </div>

          {/* Body */}
          {mode === 'chat' && (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] min-w-0 px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${m.role === 'user' ? 'bg-brand-500 text-white rounded-br-sm' : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'}`}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-white text-gray-500 border border-gray-200 px-3 py-2 rounded-2xl rounded-bl-sm text-sm">
                      <span className="inline-flex gap-1">
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '120ms' }} />
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '240ms' }} />
                      </span>
                    </div>
                  </div>
                )}
                {error && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
                )}
              </div>

              <div className="px-3 py-2 border-t border-gray-200 bg-white">
                <div className="flex items-end gap-2">
                  <textarea
                    rows={1}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="Type a question..."
                    disabled={loading}
                    className="flex-1 min-w-0 resize-none px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 disabled:bg-gray-50"
                    style={{ maxHeight: '120px' }}
                  />
                  <button
                    type="button"
                    onClick={sendMessage}
                    disabled={loading || !input.trim()}
                    className="shrink-0 px-3 py-2 bg-brand-500 hover:bg-brand-600 disabled:bg-gray-300 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Send
                  </button>
                </div>
                <div className="flex items-center justify-between mt-2 px-1">
                  <p className="text-[11px] text-gray-400 truncate">Answers are AI-generated.</p>
                  <button
                    type="button"
                    onClick={openEmail}
                    className="shrink-0 text-[12px] text-brand-600 hover:text-brand-700 hover:underline font-medium"
                  >
                    Email us instead →
                  </button>
                </div>
              </div>
            </>
          )}

          {mode === 'email' && (
            <div className="flex-1 overflow-y-auto px-4 py-4 bg-white">
              <p className="text-sm text-gray-600 mb-3">
                Send your question to {studio?.name || 'the studio'}. They'll reply directly to your email.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Your name (optional)</label>
                  <input
                    type="text"
                    value={emailForm.name}
                    onChange={(e) => setEmailForm({ ...emailForm, name: e.target.value })}
                    placeholder="Enter your name"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    value={emailForm.email}
                    onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })}
                    placeholder="Enter your email"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Your question <span className="text-red-500">*</span></label>
                  <textarea
                    rows={5}
                    value={emailForm.question}
                    onChange={(e) => setEmailForm({ ...emailForm, question: e.target.value })}
                    placeholder="Enter your question"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 resize-none"
                  />
                </div>
                {emailError && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{emailError}</div>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setMode('chat')}
                    disabled={emailSending}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 disabled:opacity-50"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={sendEmail}
                    disabled={emailSending}
                    className="flex-1 px-3 py-2 text-sm font-medium bg-brand-500 hover:bg-brand-600 disabled:bg-gray-300 text-white rounded-lg"
                  >
                    {emailSending ? 'Sending...' : 'Send to studio'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {mode === 'sent' && (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-8 bg-white">
              <div className="w-12 h-12 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6" aria-hidden="true">
                  <path fillRule="evenodd" d="M2.25 12a9.75 9.75 0 1 1 19.5 0 9.75 9.75 0 0 1-19.5 0Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Your question is on its way</h3>
              <p className="text-sm text-gray-600 mb-4">{studio?.name || 'The studio'} will reply to <span className="font-medium">{emailForm.email}</span> shortly.</p>
              <button
                type="button"
                onClick={closePanel}
                className="px-4 py-2 text-sm bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium"
              >
                Close
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SupportChat;
